import { useFetcher } from "@remix-run/react"
import { useCallback, useEffect, useState } from "react"
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
} from "~/components/ui/pagination"
import { cn } from "~/utils/shadecn"

export interface InfiniteScrollProps extends React.HTMLAttributes<HTMLDivElement> {
  action: string
  params?: {}
  items: any[]
  setItems: any
  separator?: string
  dataKey?: string
}

export interface PaginationProps extends React.HTMLAttributes<HTMLDivElement> {
  action: string
  currentPage: number
  totalPages: number
}

export const InfiniteScroll = ({
  items,
  setItems,
  action,
  params,
  children,
  separator = "?",
  dataKey = "items",
}: InfiniteScrollProps) => {
  const [scrollPosition, setScrollPosition] = useState(0)
  const [clientHeight, setClientHeight] = useState(0)
  const [height, setHeight] = useState(null)
  const [shouldFetch, setShouldFetch] = useState(true)
  const [page, setPage] = useState(2)
  const fetcher = useFetcher()

  useEffect(() => {
    setShouldFetch(true)
    setPage(2)
  }, [action])

  useEffect(() => {
    const scrollListener = () => {
      setClientHeight(window.innerHeight)
      setScrollPosition(window.scrollY)
    }

    // Avoid running during SSR
    if (typeof window !== "undefined") {
      window.addEventListener("scroll", scrollListener)
    }

    // Clean up
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("scroll", scrollListener)
      }
    }
  }, [])

  const divHeight = useCallback(
    (node) => {
      if (node !== null) {
        node.getBounding
        setHeight(node.getBoundingClientRect().height)
      }
    },
    [items?.length]
  )

  useEffect(() => {
    if (!shouldFetch || !height) return
    if (clientHeight + scrollPosition < height) return
    if (fetcher.state == "loading") return
    if (params) {
      params.page = page
      fetcher.load(
        `${action}?${Object.keys(params)
          .map((key) => `${key}=${params[key]}`)
          .join("&")}`
      )
    } else {
      fetcher.load(`${action}${separator}page=${page}`)
    }
    setShouldFetch(false)
  }, [clientHeight, scrollPosition, fetcher.state])

  useEffect(() => {
    if (!fetcher.data) return
    if (fetcher.data[dataKey]?.length === 0) {
      setShouldFetch(false)
      return
    }

    if (fetcher.data[dataKey]?.length > 0) {
      setItems((prevItems: any) => [...prevItems, ...fetcher.data[dataKey]])
      setPage((page: number) => page + 1)
      setShouldFetch(true)
    }
  }, [fetcher.data])

  return <div ref={divHeight}>{children}</div>
}

export const PaginationNav = ({ action, currentPage, totalPages, className }: PaginationProps) => {
  let pages = []

  for (let i = currentPage - 4; i <= currentPage + 4; i++) {
    if (i > 0 && i <= totalPages) {
      pages.push(i)
    }
  }

  return (
    <Pagination className={className}>
      <PaginationContent>
        <PaginationItem className={cn("invisible", currentPage > 1 && "visible")}>
          <PaginationPrevious href={`${action}?page=${currentPage - 1}`} />
        </PaginationItem>
        <PaginationItem className={cn("invisible", currentPage > 5 && "visible")}>
          <PaginationEllipsis />
        </PaginationItem>
        {pages.map((page, index) => (
          <PaginationItem key={index}>
            <PaginationLink href={`${action}?page=${page}`} isActive={currentPage === page}>
              {page}
            </PaginationLink>
          </PaginationItem>
        ))}
        <PaginationItem className={cn("invisible", currentPage < totalPages - 4 && "visible")}>
          <PaginationEllipsis />
        </PaginationItem>
        <PaginationItem className={cn("invisible", currentPage < totalPages && "visible")}>
          <PaginationNext href={`${action}?page=${currentPage + 1}`} />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  )
}
